import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { createNotification } from "../../utils/notificationManager";
import CancelModal from "./CancelModal";
import NoPlan from "./NoPlan";
import SubscriptionCardFolder from "./SubscriptionCardFolder";
import SubscriptionStatusCard from "./SubscriptionStatusCard";
import UpgradePlanModal from "./UpgradePlanModal";
import { initializePaddle } from "./Paddle";
import {
  getPaddleBillingClient,
  getPaddleBillingConfig,
  subscribeToPaddleBillingEvents,
} from "../../utils/paddleBilling";
import { getBillingPricePreviewLabel } from "./billingCatalog";
import { normalizeSubscriptionForStatusCard } from "./subscriptionPresentation";

const MyPlan = (props) => {
  const [modal, setModal] = useState({
    cancelModal: false,
    activateModal: false,
    upgradeModal: false,
  });
  const [isPaddleInitialized, setIsPaddleInitialized] = useState(false);
  const [pendingBillingMutation, setPendingBillingMutation] = useState(false);
  const visibilityStateRef = useRef(document.visibilityState);
  const location = useLocation();

  const {
    myPlan,
    isLoading,
    auth,
    manageSubscription,
    submitFeedback,
    createBillingCheckout,
    applyBillingPlanChange,
    billingPlanChangeLoading,
    billingMutationError,
    openBillingManagement: requestBillingManagement,
    getSubscriptionPlan,
    getBillingPlans,
    billingPlans,
    billingPlansLoading,
    billingPlansError,
    cancelPlan,
    upgradePlan,
    embedded = false,
  } = props;

  const isBilling = myPlan?.is_billing === true;
  const planActions = myPlan?.actions || {};
  const subscription = useMemo(
    () => normalizeSubscriptionForStatusCard(myPlan),
    [myPlan]
  );
  const billingConfig = getPaddleBillingConfig();
  const billingPriceIds = useMemo(
    () => (billingPlans || []).map((plan) => plan?.price_id).filter(Boolean),
    [billingPlans]
  );
  const billingPricePreviewCacheKey = useMemo(
    () => `${billingConfig.billingEnv}:${billingPriceIds.join("|")}`,
    [billingConfig.billingEnv, billingPriceIds]
  );
  const billingPricePreviewRequestKeyRef = useRef("");
  const [billingPriceLabels, setBillingPriceLabels] = useState({});
  const [billingPricePreviewLoading, setBillingPricePreviewLoading] = useState(false);
  const billingPricePreviewPending =
    billingPriceIds.length > 0 &&
    !billingPlansLoading &&
    !billingPlansError &&
    billingConfig.isBillingConfigured &&
    billingPricePreviewRequestKeyRef.current !== billingPricePreviewCacheKey;
  const billingPricePreviewIsLoading =
    billingPricePreviewLoading || billingPricePreviewPending;

  const refreshAfterCheckout = useCallback(
    (eventInfo) => {
      if (eventInfo?.override?.split("/").pop() === "cancel") {
        cancelPlan();
      }

      window.setTimeout(() => {
        getSubscriptionPlan({ refetch: true, silent: true });
      }, 5000);
    },
    [cancelPlan, getSubscriptionPlan]
  );

  useEffect(() => {
    if (isBilling || isPaddleInitialized) {
      return undefined;
    }

    let mounted = true;
    initializePaddle(refreshAfterCheckout)
      .then((initialized) => {
        if (mounted && initialized) {
          setIsPaddleInitialized(true);
        }
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
    };
  }, [isBilling, isPaddleInitialized, refreshAfterCheckout]);

  useEffect(() => {
    return subscribeToPaddleBillingEvents((event) => {
      if (event?.name !== "checkout.completed") {
        return;
      }

      getSubscriptionPlan({ refetch: true, silent: true });
    });
  }, [getSubscriptionPlan]);

  useEffect(() => {
    if (location.state?.subs_modal) {
      setModal((current) => ({ ...current, upgradeModal: true }));
    }
  }, [location.state?.subs_modal]);

  useEffect(() => {
    getSubscriptionPlan();
  }, [getSubscriptionPlan]);

  useEffect(() => {
    getBillingPlans();
  }, [getBillingPlans]);

  useEffect(() => {
    let cancelled = false;

    if (
      billingPlansLoading ||
      billingPlansError ||
      billingPriceIds.length === 0 ||
      !billingConfig.isBillingConfigured ||
      billingPricePreviewRequestKeyRef.current === billingPricePreviewCacheKey
    ) {
      if (billingPlansError || billingPriceIds.length === 0) {
        setBillingPriceLabels({});
      }
      setBillingPricePreviewLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setBillingPricePreviewLoading(true);
    setBillingPriceLabels({});

    getPaddleBillingClient()
      .then((paddle) =>
        paddle.PricePreview({
          items: billingPriceIds.map((priceId) => ({ priceId, quantity: 1 })),
        })
      )
      .then((preview) => {
        if (cancelled) {
          return;
        }

        const lineItems =
          preview?.data?.details?.lineItems ||
          preview?.data?.details?.line_items ||
          [];
        const nextLabels = {};
        lineItems.forEach((lineItem) => {
          const priceId = lineItem?.price?.id;
          const label = getBillingPricePreviewLabel(lineItem);
          if (priceId && label) {
            nextLabels[priceId] = label;
          }
        });

        billingPricePreviewRequestKeyRef.current = billingPricePreviewCacheKey;
        setBillingPriceLabels(nextLabels);
        setBillingPricePreviewLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          billingPricePreviewRequestKeyRef.current = billingPricePreviewCacheKey;
          setBillingPriceLabels({});
          setBillingPricePreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    billingConfig.isBillingConfigured,
    billingPlansError,
    billingPlansLoading,
    billingPriceIds,
    billingPricePreviewCacheKey,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const previous = visibilityStateRef.current;
      const next = document.visibilityState;
      visibilityStateRef.current = next;

      if (previous === "hidden" && next === "visible") {
        getSubscriptionPlan({ refetch: true, silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [getSubscriptionPlan]);

  const handleChoosePlan = useCallback(() => {
    setModal((current) => ({ ...current, upgradeModal: true }));
  }, []);

  const handleChangePlan = useCallback(() => {
    setModal((current) => ({ ...current, upgradeModal: true }));
  }, []);

  const handleManageSubscription = useCallback(() => {
    if (isBilling) {
      requestBillingManagement({ type: "manage" });
      return;
    }

    setModal((current) => ({ ...current, cancelModal: true }));
  }, [isBilling, requestBillingManagement]);

  const initiateBillingPlanChange = useCallback(
    (data) => {
      setPendingBillingMutation(true);
      applyBillingPlanChange(data);
    },
    [applyBillingPlanChange]
  );

  useEffect(() => {
    if (!pendingBillingMutation || billingPlanChangeLoading) {
      return;
    }

    if (!billingMutationError) {
      setModal((current) => ({ ...current, upgradeModal: false }));
    }

    setPendingBillingMutation(false);
  }, [billingMutationError, billingPlanChangeLoading, pendingBillingMutation]);

  const initiatePlanUpgrade = useCallback(
    (data) => {
      if (String(data?.plan_id) === String(subscription?.priceId)) {
        createNotification("warning", "You already have this plan", 2000);
        return;
      }
      upgradePlan(data);
    },
    [subscription?.priceId, upgradePlan]
  );

  const initiateSubscriptionManager = useCallback(
    (data) => {
      manageSubscription(data);
    },
    [manageSubscription]
  );

  const billingUserEmail = auth?.auth?.user?.emails?.find(
    (item) => item?.default === true
  )?.email;
  const canManage = isBilling
    ? Boolean(subscription?.canManage)
    : planActions.can_cancel !== false;

  return (
    <div
      className={embedded ? "subscription-page--embedded" : ""}
      style={
        embedded
          ? undefined
          : {
              padding: "20px 5%",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }
      }
    >
      {!isBilling && (
        <CancelModal
          cancelURL={myPlan?.cancel_url}
          modal={modal}
          setModal={setModal}
          managePauseSubscription={initiateSubscriptionManager}
          isPaused={Boolean(myPlan?.payment_information?.paused_at)}
          submitFeedback={submitFeedback}
          isLoading={isLoading}
          actions={{ ...planActions, can_cancel: canManage }}
        />
      )}

      <UpgradePlanModal
        modal={modal}
        setModal={setModal}
        planStatus={myPlan?.status}
        planId={subscription?.priceId}
        currentPlanUnitPrice={myPlan?.unit_price}
        currentPlanCurrency={myPlan?.currency}
        currentPlanName={myPlan?.plan_name}
        isBilling={isBilling}
        billingPlans={billingPlans}
        billingPlansLoading={billingPlansLoading}
        billingPlansError={billingPlansError}
        billingPriceLabels={billingPriceLabels}
        billingPricePreviewLoading={billingPricePreviewIsLoading}
        retryBillingPlans={getBillingPlans}
        billingActions={planActions}
        userMail={billingUserEmail}
        initiatePlanUpgrade={initiatePlanUpgrade}
        initiateBillingCheckout={createBillingCheckout}
        billingPlanChangeLoading={billingPlanChangeLoading}
        billingMutationError={billingMutationError}
        confirmBillingPlanChange={initiateBillingPlanChange}
      />

      <div className="dashboardHeader subscription-page-heading">
        <div>
          <span className="homeHead">My Subscription Plan</span>
        </div>
      </div>

      <div className="subscription-page-content">
        {isLoading && !myPlan ? (
          <div className="subscription-overview-grid">
            <SubscriptionStatusCard subscription={undefined} />
            <SubscriptionCardFolder subscription={undefined} />
          </div>
        ) : subscription ? (
          <>
            <div className="subscription-overview-grid">
              <SubscriptionStatusCard
                subscription={subscription}
                onChangePlan={subscription.canChangePlan ? handleChangePlan : undefined}
                onManageSubscription={canManage ? handleManageSubscription : undefined}
                onChoosePlan={subscription.status === "canceled" ? handleChoosePlan : undefined}
              />
              <SubscriptionCardFolder
                subscription={subscription}
                onAction={canManage ? handleManageSubscription : undefined}
              />
            </div>
          </>
        ) : (
          <NoPlan memberSince={myPlan?.created_at} setModal={setModal} />
        )}
      </div>
    </div>
  );
};

export default MyPlan;
